import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { Message } from 'primeng/message';
import type { CompilableQuestion } from '../../../services/compiling/apply-compiled-answer';
import { BulkCompileService } from '../../../services/compiling/bulk-compile.service';
import { CompilingDashboardService } from '../compiling-dashboard.service';

/**
 * The review dialog for a bulk compile: where its proposals are read and
 * applied. Nothing else.
 *
 * Everything that used to sit beside it in a card docked to the corner is gone.
 * Progress belongs to the global run tracker, which follows the applicant off
 * this page as the server-side batch does. What is left — a run that was refused
 * before it started, and the reminder that applying saves nothing — goes through
 * the dashboard's toast, which says its piece and gets out of the way. A card
 * parked over the form until dismissed was one more thing to close.
 *
 * The host is `display: contents`, so the component adds no box of its own to
 * the dashboard's flex row. The trigger, and the way back into a review that was
 * closed, are `BulkCompileButtonComponent`.
 */
@Component({
  selector: 'lib-bulk-compile-status',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
  imports: [
    ButtonModule,
    DialogModule,
    Message,
    NgTemplateOutlet,
  ],
  template: `
    <!-- One definition of the "jump to this question" link, used by all four
         result sections below. -->
    <ng-template #goToLink let-question>
      @if (question.sectionId) {
        <button
          type="button"
          class="cursor-pointer self-start text-sm font-medium underline hover:no-underline"
          (click)="goTo(question)"
        >
          Vai alla domanda
        </button>
      }
    </ng-template>

    <p-dialog
      header="Esito della compilazione automatica"
      [visible]="resultsVisible()"
      (visibleChange)="setResultsVisible($event)"
      [modal]="true"
      [style]="{ width: '680px' }"
      [draggable]="false"
    >
      @if (bulk.outcome(); as result) {
        <div class="flex flex-col gap-4" data-tour="bulk-compile-results">
          @if (result.compiled.length) {
            <section class="flex flex-col gap-2">
              <h3 class="m-0 text-base font-medium">
                {{ result.compiled.length }}
                {{
                  result.applied ? 'risposte compilate' : 'risposte proposte'
                }}
              </h3>
              @if (!result.applied) {
                <p class="m-0 text-sm text-gray-500">
                  Le risposte attuali verranno sostituite da quelle qui sotto.
                </p>
              }
              <ul class="m-0 flex list-none flex-col gap-3 p-0">
                @for (item of result.compiled; track item.widgetId) {
                  <li
                    class="flex flex-col gap-1 rounded-xl border border-gray-200 p-3"
                  >
                    <span class="text-sm font-medium">{{ item.label }}</span>
                    @if (item.preview) {
                      <span
                        class="line-clamp-3 whitespace-pre-wrap text-sm text-gray-600"
                        >{{ item.preview }}</span
                      >
                    }
                    <ng-container
                      [ngTemplateOutlet]="goToLink"
                      [ngTemplateOutletContext]="{ $implicit: item }"
                    />
                  </li>
                }
              </ul>
            </section>
          }

          <!-- Answered, but with nothing: the model looked and found no evidence.
               Listed apart from the proposals so it is neither counted among
               them nor applied, and apart from the failures because retrying
               would find the same nothing — the reason is the whole result. -->
          @if (result.noProposal.length; as count) {
            <section class="flex flex-col gap-2">
              <h3 class="m-0 text-base font-medium">
                {{ count }}
                {{
                  count === 1
                    ? 'domanda senza proposta'
                    : 'domande senza proposta'
                }}
              </h3>
              <p class="m-0 text-sm text-gray-500">
                L'AI non ha trovato nei documenti elementi sufficienti per
                rispondere. La risposta attuale resta invariata.
              </p>
              <ul class="m-0 flex list-none flex-col gap-2 p-0">
                @for (item of result.noProposal; track item.widgetId) {
                  <li class="flex flex-col gap-0.5">
                    <span class="text-sm font-medium">{{ item.label }}</span>
                    @if (item.justification) {
                      <span
                        class="line-clamp-4 whitespace-pre-wrap text-sm text-gray-600"
                        >{{ item.justification }}</span
                      >
                    }
                    <ng-container
                      [ngTemplateOutlet]="goToLink"
                      [ngTemplateOutletContext]="{ $implicit: item }"
                    />
                  </li>
                }
              </ul>
            </section>
          }

          @if (result.alreadyAnsweredCount; as skipped) {
            <p-message severity="info" styleClass="w-full">
              <span class="text-sm">
                {{ skipped }}
                {{
                  skipped === 1
                    ? 'domanda già compilata è stata saltata'
                    : 'domande già compilate sono state saltate'
                }}. Scegli "Rigenera tutte le risposte" per rifarle.
              </span>
            </p-message>
          }

          @if (result.unsupported.length) {
            <section class="flex flex-col gap-2">
              <h3 class="m-0 text-base font-medium">
                Da compilare manualmente
              </h3>
              <p class="m-0 text-sm text-gray-500">
                L'AI non può rispondere a queste domande: vanno compilate a
                mano.
              </p>
              <ul class="m-0 flex list-none flex-col gap-2 p-0">
                @for (item of result.unsupported; track item.widgetId) {
                  <li class="flex flex-col gap-0.5">
                    <span class="text-sm">{{ item.label }}</span>
                    <ng-container
                      [ngTemplateOutlet]="goToLink"
                      [ngTemplateOutletContext]="{ $implicit: item }"
                    />
                  </li>
                }
              </ul>
            </section>
          }

          @if (result.problems.length) {
            <section class="flex flex-col gap-2">
              <h3 class="m-0 text-base font-medium">Domande non riuscite</h3>
              <p class="m-0 text-sm text-gray-500">
                Le altre risposte restano valide. Riprova queste singolarmente
                con il pulsante "Compila automaticamente" della domanda.
              </p>
              <ul class="m-0 flex list-none flex-col gap-2 p-0">
                @for (item of result.problems; track item.widgetId) {
                  <li class="flex flex-col gap-0.5">
                    <span class="text-sm font-medium">{{ item.label }}</span>
                    <span class="text-sm text-gray-600">{{
                      item.message
                    }}</span>
                    <ng-container
                      [ngTemplateOutlet]="goToLink"
                      [ngTemplateOutletContext]="{ $implicit: item }"
                    />
                  </li>
                }
              </ul>
            </section>
          }

          @if (isEmptyResult()) {
            <p class="m-0 text-sm text-gray-600">
              Non è stata generata nessuna nuova risposta.
            </p>
          }
        </div>
      }

      <ng-template pTemplate="footer">
        @let result = bulk.outcome();
        <div class="flex justify-end gap-2">
          @if (result && !result.applied && result.compiled.length) {
            <p-button
              label="Annulla"
              severity="secondary"
              [text]="true"
              (onClick)="setResultsVisible(false)"
            />
            <p-button
              [label]="'Applica ' + result.compiled.length + ' risposte'"
              data-tour="bulk-compile-apply"
              (onClick)="bulk.applySuggestions()"
            />
          } @else {
            <p-button
              label="Ok"
              severity="secondary"
              [text]="true"
              (onClick)="setResultsVisible(false)"
            />
          }
        </div>
      </ng-template>
    </p-dialog>
  `,
})
export class BulkCompileStatusComponent {
  protected readonly bulk = inject(BulkCompileService);
  private readonly dashboard = inject(CompilingDashboardService, {
    optional: true,
  });
  private readonly messages = inject(MessageService);

  /**
   * Opens itself whenever results land or something asks for the review again,
   * and stays user-writable in between so "Chiudi" and "Dettagli" work without a
   * second signal.
   */
  protected readonly resultsVisible = linkedSignal({
    source: () => [this.bulk.outcome(), this.bulk.reviewRequests()] as const,
    computation: ([outcome]) => !!outcome,
  });


  /**
   * The review is closing to show a question, not because the applicant is done
   * with it.
   *
   * Closing the review normally *discards* the proposals, and now also
   * acknowledges the run server-side — so without this flag "Vai alla domanda"
   * threw the whole compilation away, permanently, on the one click whose entire
   * purpose is to go and look before deciding.
   */
  private goingToQuestion = false;

  /**
   * A start that never became a run: "Carica almeno un documento", a batch
   * already in flight, a demo allowance spent. The request refuses these, so no
   * run exists and the tracker has nothing to show — without this the button
   * would appear to do nothing at all.
   *
   * Cleared as it is shown, so the next attempt does not look pre-failed.
   */
  private readonly reportFailedStart = effect(() => {
    const error = this.bulk.error();
    if (!error) return;

    this.messages.add({
      severity: 'warn',
      summary: 'Compilazione non disponibile',
      detail: error,
      life: 8000,
    });

    this.bulk.clearError();
  });

  /**
   * Applying writes into the form and persists nothing, so the applicant still
   * has to save. By this point the run has been acknowledged and its tracker row
   * is gone, so nothing else would say it.
   */
  private readonly remindToSave = effect(() => {
    const outcome = this.bulk.outcome();
    if (!outcome?.applied || outcome.compiled.length === 0) return;

    const count = outcome.compiled.length;

    this.messages.add({
      severity: 'success',
      summary: 'Compilazione automatica completata',
      detail:
        (count === 1
          ? '1 risposta \u00e8 stata compilata automaticamente.'
          : `${count} risposte sono state compilate automaticamente.`) +
        ' Ricordati di salvare la bozza.',
      life: 8000,
    });
  });

  protected readonly isEmptyResult = computed(() => {
    const result = this.bulk.outcome();
    if (!result) return false;
    return (
      result.compiled.length === 0 &&
      result.noProposal.length === 0 &&
      result.unsupported.length === 0 &&
      result.problems.length === 0
    );
  });

  protected setResultsVisible(visible: boolean): void {
    this.resultsVisible.set(visible);

    if (visible) {
      this.goingToQuestion = false;
      return;
    }

    // Stepping out to look at a question is not a decision about the proposals.
    // The card's "Dettagli" brings the review straight back.
    if (this.goingToQuestion) {
      this.goingToQuestion = false;
      return;
    }

    // Closing the review without applying throws the proposals away, and tells
    // the server the run has been dealt with.
    const result = this.bulk.outcome();
    if (result && !result.applied) this.bulk.dismissOutcome();
  }

  protected goTo(question: CompilableQuestion): void {
    this.goingToQuestion = true;
    this.setResultsVisible(false);
    this.dashboard?.focusQuestion(question);
  }
}
